import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useRef, useState } from 'react';
import { defaultCountries } from 'shared/react-international-phone/constants/default-countries';
import { DEFAULT_COUNTRY } from 'shared/react-international-phone/constants/default-country';
import { buildClassNames } from '../../style/build-class-names';
import { CountryData, CountryIso2 } from '../../types';
import { getCountry } from '../../utils';
import { FlagEmoji } from '../flag-emoji/flag-emoji';
import { CountrySelectorDropdown, CountrySelectorDropdownProps, CountrySelectorDropdownStyleProps } from './country-selector-dropdown';
import './country-selector.module.css';
export interface CountrySelectorStyleProps {
  style?: React.CSSProperties;
  className?: string;
  buttonStyle?: React.CSSProperties;
  buttonClassName?: string;
  buttonContentWrapperStyle?: React.CSSProperties;
  buttonContentWrapperClassName?: string;
  flagStyle?: React.CSSProperties;
  flagClassName?: string;
  dropdownArrowStyle?: React.CSSProperties;
  dropdownArrowClassName?: string;
  dropdownStyleProps?: CountrySelectorDropdownStyleProps;
}
type RenderButtonWrapperRootProps = {
  // Omit the event argument to prevent errors on event mistype
  onClick: () => void;
} & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onKeyDown' | 'onMouseDown' | 'title' | 'disabled' | 'role' | 'aria-label' | 'aria-haspopup' | 'aria-expanded'>;
export interface CountrySelectorProps extends CountrySelectorStyleProps {
  selectedCountry: CountryIso2;
  onSelect?: CountrySelectorDropdownProps['onSelect'];
  disabled?: boolean;
  hideDropdown?: boolean;
  defaultCountry?: CountryIso2;
  countries?: CountryData[];
  renderButtonWrapper?: (props: {
    children: React.ReactNode;
    rootProps: RenderButtonWrapperRootProps;
  }) => React.ReactNode;
}
export const CountrySelector: React.FC<CountrySelectorProps> = t0 => {
  const $ = _c(23);
  const {
    selectedCountry,
    onSelect,
    disabled,
    hideDropdown,
    defaultCountry: t1,
    countries: t2,
    renderButtonWrapper,
    ...styleProps
  } = t0;
  const defaultCountry = t1 === undefined ? DEFAULT_COUNTRY : t1;
  const countries = t2 === undefined ? defaultCountries : t2;
  const [showDropdown, setShowDropdown] = useState(false);
  let t3;
  bb0: {
    if (!selectedCountry) {
      t3 = undefined;
      break bb0;
    }
    let t4;
    if ($[0] !== countries || $[1] !== defaultCountry || $[2] !== selectedCountry) {
      t4 = getCountry({
        value: selectedCountry,
        field: "iso2",
        countries,
        defaultCountry
      });
      $[0] = countries;
      $[1] = defaultCountry;
      $[2] = selectedCountry;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    t3 = t4;
  }
  const fullSelectedCountry = t3;
  const countrySelectorRef = useRef(null);
  let t4;
  if ($[4] !== setShowDropdown) {
    t4 = e => {
      if (!e.key) {
        return;
      }
      if (["ArrowUp", "ArrowDown"].includes(e.key)) {
        e.preventDefault();
        setShowDropdown(true);
      }
    };
    $[4] = setShowDropdown;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleKeyDown = t4;
  const renderSelectorButton = () => {
    const rootProps = {
      title: fullSelectedCountry?.name,
      onClick: () => setShowDropdown(_temp),
      onMouseDown: _temp2,
      onKeyDown: handleKeyDown,
      disabled: hideDropdown || disabled,
      role: "combobox",
      "aria-label": "Country selector",
      "aria-haspopup": "listbox",
      "aria-expanded": showDropdown
    };
    const buttonContent = <div className={buildClassNames({
      addPrefix: ["country-selector-button__button-content"],
      rawClassNames: [styleProps.buttonContentWrapperClassName]
    })} style={styleProps.buttonContentWrapperStyle}><FlagEmoji iso2={selectedCountry} className={buildClassNames({
        addPrefix: ["country-selector-button__flag-emoji", disabled && "country-selector-button__flag-emoji--disabled"],
        rawClassNames: [styleProps.flagClassName]
      })} style={{
        visibility: selectedCountry ? "visible" : "hidden",
        ...styleProps.flagStyle
      }} />{!hideDropdown && <div className={buildClassNames({
        addPrefix: ["country-selector-button__dropdown-arrow", disabled && "country-selector-button__dropdown-arrow--disabled", showDropdown && "country-selector-button__dropdown-arrow--active"],
        rawClassNames: [styleProps.dropdownArrowClassName]
      })} style={styleProps.dropdownArrowStyle} />}</div>;
    if (renderButtonWrapper) {
      return renderButtonWrapper({
        children: buttonContent,
        rootProps
      });
    }
    return <button {...rootProps} type="button" className={buildClassNames({
      addPrefix: ["country-selector-button", showDropdown && "country-selector-button--active", disabled && "country-selector-button--disabled", hideDropdown && "country-selector-button--hide-dropdown"],
      rawClassNames: [styleProps.buttonClassName]
    })} data-country={selectedCountry} style={styleProps.buttonStyle}>{buttonContent}</button>;
  };
  const t5 = buildClassNames({
    addPrefix: ["country-selector"],
    rawClassNames: [styleProps.className]
  });
  const t6 = styleProps.style;
  const t7 = renderSelectorButton();
  let t8;
  if ($[6] !== onSelect || $[7] !== setShowDropdown) {
    t8 = country => {
      setShowDropdown(false);
      onSelect?.(country);
    };
    $[6] = onSelect;
    $[7] = setShowDropdown;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== setShowDropdown) {
    t9 = () => {
      setShowDropdown(false);
    };
    $[9] = setShowDropdown;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== countries || $[12] !== selectedCountry || $[13] !== showDropdown || $[14] !== styleProps.dropdownStyleProps || $[15] !== t8 || $[16] !== t9) {
    t10 = <CountrySelectorDropdown show={showDropdown} countries={countries} onSelect={t8} selectedCountry={selectedCountry} onClose={t9} {...styleProps.dropdownStyleProps} />;
    $[11] = countries;
    $[12] = selectedCountry;
    $[13] = showDropdown;
    $[14] = styleProps.dropdownStyleProps;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== styleProps.style || $[19] !== t10 || $[20] !== t5 || $[21] !== t7) {
    t11 = <div className={t5} style={t6} ref={countrySelectorRef}>{t7}{t10}</div>;
    $[18] = styleProps.style;
    $[19] = t10;
    $[20] = t5;
    $[21] = t7;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
};
function _temp(v) {
  return !v;
}
function _temp2(e_0) {
  return e_0.preventDefault();
}